import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Heading, SimpleGrid } from '@chakra-ui/react';
import { editRenewable } from '../../../../services/renewables';
import { enUSFormatExcludingTime, lenientParse } from '../../../../services/items';
import { DEFAULTFLATWEIGHT } from 'constants/renewables';
import { SLICEOFDAY_PARTIAL_DELIVERABILITY } from 'constants/autoforms';
import { format } from 'date-fns';
import { useFormikContext } from 'formik';
import debounce from 'lodash/debounce';

import ComboboxFormikField from '../../../forms/ComboboxFormikField';
import DateFormikField from '../../../forms/DateFormikField';
import NumberInputFormikField from '../../../forms/NumberInputFormikField';
import useItemCreationWizard from 'hooks/useItemCreationWizard';
import { FEATURE_FLAG_NAMES, useIsFeatureActive } from 'hooks/features-flags';

const RenewableDefinitionPanel = ({ idx, definitions, type, formLabels, helpPageUrlItemTypeId = null }) => {
    const fieldNamePrefix = `definitions.${idx}`;
    const intl = useIntl();
    const { values, resetForm } = useFormikContext();
    const previousPcdsPercentRef = useRef();

    const isCAMSliceOfDayVisible = useIsFeatureActive(FEATURE_FLAG_NAMES.CAM_SLICEOFDAY_FIELDS_VISIBLE);
    const [pcdsPercentDisabled, setPcdsPercentDisabled] = useState(true);

    const {
        itemTypeId: selectedItemTypeId,
        newItemId,
        availableWeatherZones,
        projectOnOffDates,
        updateAvailableWeatherZones,
    } = useItemCreationWizard();

    const updateDefinitions = useCallback(async () => {
        const subRenewablePayload = {
            renewableId: definitions.renewableDefinitionResponses[idx].id,
            description: definitions.renewableDefinitionResponses[idx].description,
            weatherZoneId: definitions.renewableDefinitionResponses[idx].weatherZoneId,
            moduleProcessId: definitions.renewableDefinitionResponses[idx].moduleProcessId,
            modelWeightId: DEFAULTFLATWEIGHT,
            plantTypeId: definitions.renewableDefinitionResponses[idx].plantTypeId,
            deliverabilityStatusId: definitions.renewableDefinitionResponses[idx].deliverabilityStatusId,
            pcdsPercent: definitions.renewableDefinitionResponses[idx].pcdsPercent,
        };
        var payload = {
            renewable: {
                description: definitions.renewableItem.description,
                bookId: definitions.renewableItem.bookId,
                itemTypeId: selectedItemTypeId,
                pricingPointId: definitions.renewableItem.pricingPointId,
                itemId: newItemId,
                longitude: definitions.renewableItem.longitude !== '' ? definitions.renewableItem.longitude : null,
                latitude: definitions.renewableItem.latitude !== '' ? definitions.renewableItem.latitude : null,
                securityId: definitions.renewableItem.securityId,
                rtelecBasisPriceId: definitions.renewableItem.rtelecBasisPriceId,
            },
            subRenewables: [{ ...subRenewablePayload }],
        };

        await editRenewable(payload);
    }, [idx, definitions, selectedItemTypeId, newItemId]);

    const moduleProcessIdChanged = async (value) => {
        definitions.renewableDefinitionResponses[idx].moduleProcessId = value;
        await updateDefinitions();
    };

    const weatherzoneIdChanged = async (value) => {
        definitions.renewableDefinitionResponses[idx].weatherZoneId = value;
        await updateDefinitions();
    };

    const plantTypeIdChanged = async (value) => {
        definitions.renewableDefinitionResponses[idx].plantTypeId = value;
        await updateDefinitions();
    };

    const deliverabilityStatusIdChanged = useCallback(
        async (value) => {
            if (!isCAMSliceOfDayVisible) {
                return;
            }

            const selectedDeliverability = definitions?.deliverabilityStatusOptions
                ?.find((x) => x.id === value)
                ?.description?.trim()
                .toUpperCase();

            // Update disabled state for pcdsPercent field
            if (SLICEOFDAY_PARTIAL_DELIVERABILITY === selectedDeliverability) {
                setPcdsPercentDisabled(false);
            } else {
                setPcdsPercentDisabled(true);
                // To prevent errors, set pcdsPercent to null if it's empty string or already null
                const currentPcdsPercent = definitions.renewableDefinitionResponses[idx].pcdsPercent;
                if (currentPcdsPercent === '' || currentPcdsPercent === null || currentPcdsPercent === undefined) {
                    definitions.renewableDefinitionResponses[idx].pcdsPercent = null;
                }
            }

            // Update the definition
            definitions.renewableDefinitionResponses[idx].deliverabilityStatusId = value;

            // Only call API to update deliverability status if the status is not partial
            // or it is partial deliverability AND pcdsPercent has a value
            const currentPcdsPercent = definitions.renewableDefinitionResponses[idx].pcdsPercent;
            const isPartial = SLICEOFDAY_PARTIAL_DELIVERABILITY === selectedDeliverability;
            const shouldCallApi =
                !isPartial ||
                (currentPcdsPercent !== null && currentPcdsPercent !== undefined && currentPcdsPercent !== '');

            if (shouldCallApi) {
                await updateDefinitions();
            }
        },
        [idx, definitions, updateDefinitions, isCAMSliceOfDayVisible]
    );

    const pcdsPercentChanged = useCallback(
        async (value) => {
            // Extract the actual value if it's an event object
            const actualValue = value?.target?.value !== undefined ? value.target.value : value;

            // Update the definition
            definitions.renewableDefinitionResponses[idx].pcdsPercent = actualValue;

            // Don't call API if deliverabilityStatusId is not set or if value is empty/null
            const deliverabilityStatusId = definitions.renewableDefinitionResponses[idx].deliverabilityStatusId;

            if (!deliverabilityStatusId) {
                return;
            }

            // Don't call API if the value is null, undefined, or empty string to prevent errors
            if (actualValue === null || actualValue === undefined || actualValue === '') {
                return;
            }

            await updateDefinitions();

            // Reset form with updated initialValues to clear dirty state
            // This prevents the Discard Changes prompt when saving the item
            resetForm({
                values: values,
                touched: {},
            });
        },
        [idx, definitions, updateDefinitions, resetForm, values]
    );

    const debouncedPcdsPercentChanged = useMemo(() => debounce(pcdsPercentChanged, 800), [pcdsPercentChanged]);

    const onlineDateChanged = useCallback(
        async (value) => {
            definitions.renewableDefinitionResponses[idx].onlineDate = format(
                lenientParse(value),
                enUSFormatExcludingTime
            );
        },
        [idx, definitions]
    );

    const offlineDateChanged = useCallback(
        async (value) => {
            definitions.renewableDefinitionResponses[idx].offlineDate = format(
                lenientParse(value),
                enUSFormatExcludingTime
            );
        },
        [idx, definitions]
    );

    useEffect(() => {
        if (!Array.isArray(availableWeatherZones) || availableWeatherZones.length === 0) {
            updateAvailableWeatherZones();
        }
        if (isCAMSliceOfDayVisible) {
            const selectedDeliverability = definitions?.deliverabilityStatusOptions
                ?.find((x) => x.id === definitions.renewableDefinitionResponses[idx].deliverabilityStatusId)
                ?.description?.trim()
                .toUpperCase();
            if (SLICEOFDAY_PARTIAL_DELIVERABILITY === selectedDeliverability) {
                setPcdsPercentDisabled(false);
            } else {
                setPcdsPercentDisabled(true);
            }
        }
    }, [
        availableWeatherZones,
        updateAvailableWeatherZones,
        projectOnOffDates,
        isCAMSliceOfDayVisible,
        definitions,
        idx,
        deliverabilityStatusIdChanged,
    ]);

    // Watch for pcdsPercent changes in Formik values and call the API with debouncing
    useEffect(() => {
        if (!isCAMSliceOfDayVisible) {
            return;
        }

        const currentPcdsPercent = values?.definitions?.[idx]?.pcdsPercent;

        // Skip on initial mount or if value hasn't actually changed
        if (previousPcdsPercentRef.current === currentPcdsPercent) {
            return;
        }

        // Skip on first render (when previousPcdsPercentRef is undefined)
        if (previousPcdsPercentRef.current === undefined) {
            previousPcdsPercentRef.current = currentPcdsPercent;
            return;
        }

        previousPcdsPercentRef.current = currentPcdsPercent;

        // Call the debounced function
        debouncedPcdsPercentChanged(currentPcdsPercent);

        // Cleanup on unmount to cancel pending debounced calls
        return () => {
            debouncedPcdsPercentChanged.cancel();
        };
    }, [values?.definitions, debouncedPcdsPercentChanged, idx, isCAMSliceOfDayVisible]);

    const validateDeliverability = (value) => {
        let errorMessage = '';

        if (!value) {
            errorMessage = intl.formatMessage(
                { id: 'common_forms_validation_required' },
                { label: intl.formatMessage({ id: 'renewable_deliverability_status' }) }
            );
        }

        return errorMessage;
    };

    return (
        <>
            <Heading as="h4" variant="h4" textTransform="capitalize">
                <FormattedMessage id="renewable_definitions_heading" values={{ renewable: type }} />
            </Heading>
            <SimpleGrid columns={{ xl: 4 }} spacingX={6}>
                <ComboboxFormikField
                    id={`${fieldNamePrefix}.moduleProcessId`}
                    name={`${fieldNamePrefix}.moduleProcessId`}
                    mt={3}
                    label={formLabels.modelType}
                    options={definitions.modelTypes}
                    valueKey="id"
                    labelKey="labelName"
                    isFastField
                    validateOnBlur={false}
                    isRequired
                    onChange={moduleProcessIdChanged}
                    showPlaceholder={false}
                />

                <ComboboxFormikField
                    id={`${fieldNamePrefix}.weatherZoneId`}
                    name={`${fieldNamePrefix}.weatherZoneId`}
                    mt={3}
                    label={formLabels.weatherZone}
                    options={availableWeatherZones}
                    valueKey="id"
                    labelKey="name"
                    isFastField
                    validateOnBlur={false}
                    isRequired
                    onChange={weatherzoneIdChanged}
                    showPlaceholder={false}
                />

                <DateFormikField
                    id={`${fieldNamePrefix}.onlineDate`}
                    name={`${fieldNamePrefix}.onlineDate`}
                    label={formLabels.onlineDate}
                    isRequired={false}
                    showTimeInput={false}
                    isDisabled={false}
                    dateFormat={enUSFormatExcludingTime}
                    info={<FormattedMessage id="item_creation_wizard_renewables_online_date" />}
                    mt={3}
                    isInPortal={true}
                    onChange={onlineDateChanged}
                    placeholderText=""
                />

                <DateFormikField
                    id={`${fieldNamePrefix}.offlineDate`}
                    name={`${fieldNamePrefix}.offlineDate`}
                    label={formLabels.offlineDate}
                    isRequired={false}
                    showTimeInput={false}
                    isDisabled={false}
                    dateFormat={enUSFormatExcludingTime}
                    info={<FormattedMessage id="item_creation_wizard_renewables_offline_date" />}
                    mt={3}
                    isInPortal={true}
                    onChange={offlineDateChanged}
                    placeholderText=""
                />

                <ComboboxFormikField
                    id={`${fieldNamePrefix}.modelWeightId`}
                    name={`${fieldNamePrefix}.modelWeightId`}
                    mt={3}
                    label={formLabels.dataWeight}
                    options={definitions.dataWeightings.filter((x) => x.id === DEFAULTFLATWEIGHT)}
                    labelKey="description"
                    valueKey="id"
                    placeholderValue=""
                    placeholderLabel={formLabels.selectPlaceholder}
                    isFastField
                    validateOnBlur={false}
                    isDisabled={true}
                />

                {Array.isArray(definitions?.plantTypes) && definitions?.plantTypes.length > 0 && (
                    <ComboboxFormikField
                        id={`${fieldNamePrefix}.plantTypeId`}
                        name={`${fieldNamePrefix}.plantTypeId`}
                        mt={3}
                        label={formLabels.plantType}
                        options={definitions?.plantTypes}
                        labelKey="description"
                        valueKey="id"
                        placeholderLabel="-"
                        placeholderValue={null}
                        showPlaceholder={true}
                        isFastField
                        validateOnBlur={false}
                        onChange={plantTypeIdChanged}
                    />
                )}

                {isCAMSliceOfDayVisible && (
                    <>
                        <ComboboxFormikField
                            id={`${fieldNamePrefix}.deliverabilityStatusId`}
                            name={`${fieldNamePrefix}.deliverabilityStatusId`}
                            label={<FormattedMessage id="renewable_deliverability_status" />}
                            options={definitions.deliverabilityStatusOptions}
                            validate={(value) => validateDeliverability(value)}
                            labelKey="description"
                            valueKey="id"
                            isRequired
                            mt={3}
                            onChange={deliverabilityStatusIdChanged}
                            placeholderValue={null}
                            showPlaceholder={true}
                        />
                        <NumberInputFormikField
                            id={`${fieldNamePrefix}.pcdsPercent`}
                            name={`${fieldNamePrefix}.pcdsPercent`}
                            keepWithinRange={true}
                            min={0}
                            max={100}
                            step={1}
                            mt={3}
                            label={<FormattedMessage id="renewable_pcds_percent" />}
                            isDisabled={pcdsPercentDisabled}
                            isRequired={!pcdsPercentDisabled}
                        />
                    </>
                )}
            </SimpleGrid>
        </>
    );
};

export default RenewableDefinitionPanel;
